import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { API, api, formatApiErrorDetail } from "@/lib/api";
import FormRenderer from "@/components/FormRenderer";
import Logo from "@/components/Logo";

const PSB = { psb1: "Personensorgeberechtigte 1 (Mutter)", psb2: "Personensorgeberechtigte 2 (Vater)" };

export default function CosignPage() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [step, setStep] = useState("register");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    axios.get(`${API}/public/cosign/${token}`).then(({ data }) => setInfo(data)).catch(() => setNotFound(true));
  }, [token]);

  const register = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/auth/register-cosign/${token}`, { email, password });
      localStorage.setItem("kat_token", data.token);
      setStep("form");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const submit = async (payload, reset) => {
    setBusy(true);
    try {
      const consent = payload.consents[info.other_role];
      await api.post(`/registrations/cosign/${token}`, { answers: payload.answers, consent });
      reset();
      setDone(true);
      toast.success("Vielen Dank! Ihre Zustimmung wurde gespeichert.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  if (notFound) return <Centered><p className="text-muted-foreground">Dieser Link ist ungültig.</p></Centered>;
  if (!info) return <Centered><p className="text-muted-foreground">Wird geladen…</p></Centered>;
  if (info.completed && !done) return <Centered><p className="text-muted-foreground">Diese Anmeldung wurde bereits von beiden Personensorgeberechtigten abgeschlossen.</p></Centered>;
  if (info.expired) return <Centered><p className="text-muted-foreground">Der Anmeldezeitraum ist abgelaufen.</p></Centered>;
  if (done) return <Centered><p className="text-lg font-semibold">Vielen Dank!</p><p className="text-muted-foreground mt-1">Die Anmeldung ist nun vollständig. Sie können dieses Fenster schließen.</p></Centered>;

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-2xl mx-auto px-6 py-10">
        <Logo className="h-12 mb-6" />
        <h1 className="text-2xl font-serif mb-1">Mit-Anmeldung: {info.kurs_name}</h1>
        <p className="text-sm text-muted-foreground mb-6">{info.kurs_gemeinde} · Sie stimmen als {PSB[info.other_role]} zu.</p>

        {step === "register" ? (
          <form onSubmit={register} className="bg-white border border-border rounded-lg shadow-sm p-6 space-y-4" data-testid="cosign-register-form">
            <p className="text-sm text-muted-foreground">Legen Sie ein eigenes Konto an (E-Mail + Passwort), um Ihre Zustimmung zu geben.</p>
            <div className="space-y-2"><Label>E-Mail-Adresse</Label><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="cosign-email" /></div>
            <div className="space-y-2"><Label>Passwort</Label><Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} data-testid="cosign-password" /></div>
            <Button type="submit" disabled={busy} data-testid="cosign-register-button">{busy ? "Wird angelegt…" : "Weiter"}</Button>
          </form>
        ) : (
          <div className="bg-white border border-border rounded-lg shadow-sm p-6">
            <FormRenderer
              courseId={null}
              fields={info.form_fields_verteilt}
              onSubmit={submit}
              submitting={busy}
              reference={{ fields: info.form_fields_verteilt, answers: info.existing_answers, title: `Angaben ${PSB[info.initiator_role]}` }}
              consents={[{ key: info.other_role, label: PSB[info.other_role] }]}
              submitLabel="Zustimmen & abschließen"
            />
          </div>
        )}
      </div>
    </div>
  );
}

function Centered({ children }) {
  return <div className="min-h-screen flex flex-col items-center justify-center text-center px-6">{children}</div>;
}
